import React, { useEffect, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { ApplicationState } from '../../store'
import { EmployeeAvatar } from '../../styles/components/employee'
import { getEmployeeFullName } from '../employees/Employees'
import { formatAvailableTime } from '../availableTimes/AvailableTimes'
import { Separator } from '../../styles/components/separator'
import { Box } from '../layout/Box'
import { Typography } from '../layout/Typography'
import Input from '../layout/Input'
import { getLocalStorageUserToken, removeLocalStorageUserToken } from '../../utils/localStorage'
import { fetchUserData } from '../../api/auth'
import { Loading } from '../data/Loading'
import { getSalonGuidFromUrlParams } from '../../utils/urlParams'
import { getTotalServicesPrice } from '../../utils/services'
import { ErrorMessage } from '../data/ErrorMessage'
import { Card } from '../layout/Card'

import { Gender } from '../../store/user/types'
import { userLogin } from '../../store/user/actions'
import { Employee } from '../../store/employees/types'
import { clearSelected as clearSelectedEmployees } from '../../store/employees/actions'
import { Service } from '../../store/services/types'
import { clearSelected as clearSelectedServices } from '../../store/services/actions'
import { AvailableTime } from '../../store/availableTimes/types'
import { clearSelected as clearSelectedAvailableTimes } from '../../store/availableTimes/actions'
import { ActionButton, SubmitButton } from '../../styles/components/buttons'
import { BookingConfirmationResponseType, confirmBookingForNewUser, confirmBookingForRegisteredUser } from '../../api/bookingConfirmation'
import { GenderSelect } from '../data/GenderSelect'
import { DateSelect } from '../data/DateSelect'
import { getBirthdayIsoString } from '../../utils/date'
import { PrivacyPolicyCheckbox } from '../data/PrivacyPolicyCheckbox'
import LoginForm from '../auth/login/LoginForm'
import { submitLogin } from '../auth/login/submitLogin'
import { PhoneInput } from '../data/PhoneInput'
import { checkIfPhoneIsValid } from '../../utils/validations/phone'
import { checkUserStatus } from '../../hooks/useUserLoggedInCheck'

interface PropsFromState {
  selectedEmployee?: Employee
  selectedDate: Date
  selectedTime?: AvailableTime
  selectedServices?: Service[]
  salonGuid?: string
}

interface OwnProps {
  handleSubmit: (employee: Employee) => void
}

type AllProps = PropsFromState & OwnProps

const BookingConfirmation: React.FC<AllProps> = ({
  selectedDate,
  selectedEmployee,
  selectedServices,
  selectedTime,
  salonGuid,
  handleSubmit
}) => {
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [gender, setGender] = useState<Gender | null>(null)
  const [birthdayDay, setBirthdayDay] = useState<string | null>(null)
  const [birthdayMonth, setBirthdayMonth] = useState<string | null>(null)
  const [birthdayYear, setBirthdayYear] = useState<string | null>(null)

  const [newsletter, setNewsletter] = useState<boolean>(true)
  const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const [loginEmail, setLoginEmail] = useState<string>('')
  const [loginPassword, setLoginPassword] = useState<string>('')
  const [loginError, setLoginError] = useState<string>('')
  const [loginLoading, setLoginLoading] = useState<boolean>(false)

  const { isLoggedIn: isUserLoggedIn, userData: storedUserData } = useSelector((state: ApplicationState) => state.user)

  const salonData = useSelector((state: ApplicationState) => state.salon.data)
  const salonGuidFromUrl = getSalonGuidFromUrlParams()

  const dispatch = useDispatch()

  const userToken: string | null = getLocalStorageUserToken()

  useEffect(() => {
    if (salonData === undefined || !salonGuidFromUrl) return

    if (!isUserLoggedIn && userToken) {
      const getUserData = async () => {
        if (!userToken) return

        setLoading(true)
        const fetchedUserData = await fetchUserData(userToken)
        setLoading(false)

        if (fetchedUserData !== null) {
          // token in localStorage is valid
          dispatch(userLogin(fetchedUserData))
        } else {
          // token in localStorage is invalid
          removeLocalStorageUserToken()
        }
      }

      getUserData()
    }
  }, [])

  if (!selectedEmployee || !selectedServices || !selectedTime || !salonGuid) return null

  // allow online booking if user is not logged in
  // or if user is logged in and not blocked
  const isUserAllowedForOnlineBooking = !isUserLoggedIn || (isUserLoggedIn && storedUserData && !storedUserData.sperre)

  const toggleNewsletter = () => {
    setNewsletter(!newsletter)
  }

  const togglePrivacyPolicy = () => {
    setPrivacyPolicy(!privacyPolicy)
  }

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setGender(null)
    setBirthdayDay(null)
    setBirthdayMonth(null)
    setBirthdayYear(null)
    setNewsletter(true)
    setPrivacyPolicy(false)
  }

  const isFormValid = () => {
    if (!isUserLoggedIn) {
      if (firstName === '') {
        setError('Bitte gib deinen Vornamen ein.')
        return false
      }

      if (lastName === '') {
        setError('Bitte gib deinen Nachnamen ein.')
        return false
      }

      if (email === '') {
        setError('Bitte gib deine E-Mail-Adresse ein.')
        return false
      }

      if (!checkIfPhoneIsValid(phone)) {
        setError('Bitte gib eine gültige Telefonnummer ein.')
        return false
      }

      if (!privacyPolicy) {
        setError('Bitte akzeptiere die Datenschutzbestimmungen.')
        return false
      }
    }
    return true
  }

  const getFullAppointmentDateAndTime = () => {
    const dateString = selectedDate.toLocaleDateString('de-DE', { weekday: 'long', month: 'long', day: 'numeric' })

    const startTime = formatAvailableTime(selectedTime.start)
    return `${dateString}, ${startTime}`
  }

  const confirmBooking = async () => {
    if (!isFormValid()) return

    setLoading(true)
    const response =
      isUserLoggedIn && storedUserData && userToken
        ? await confirmBookingForRegisteredUser({
            selectedEmployeeGuid: selectedEmployee.guid,
            selectedDateIsoString: selectedDate.toISOString(),
            salonGuid,
            selectedTimeStart: selectedTime.start,
            selectedServices,
            userToken
          })
        : await confirmBookingForNewUser({
            selectedEmployeeGuid: selectedEmployee.guid,
            selectedDateIsoString: selectedDate.toISOString(),
            salonGuid,
            selectedTimeStart: selectedTime.start,
            firstName,
            lastName,
            phone,
            email,
            gender: gender || Gender.Male,
            birthday: getBirthdayIsoString(birthdayDay || '1', birthdayMonth || '0', birthdayYear || '1970'),
            newsletter,
            selectedServices
          })

    setLoading(false)

    if (response === null) {
      setError('Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.')
      return
    }

    if (response.type === BookingConfirmationResponseType.AlreadyBooked) {
      setError('Oh nein! Dieser Termin ist bereits vergeben. Bitte wähle einen anderen Termin aus.')
      return
    }

    if (response.type === BookingConfirmationResponseType.Unauthorized) {
      setError('')
      checkUserStatus({ dispatch })
      return
    }

    if (response.type === BookingConfirmationResponseType.UserIsBlocked) {
      setError(
        response.message ||
          `Der Termin ist nicht online buchbar. Nur telefonisch möglich. Bitte ${salonData ? salonData.kontakt.tel : '0'} anrufen.`
      )
      return
    }

    if (response !== null) {
      resetForm()
      setError('')
      handleSubmit(selectedEmployee)
      dispatch(clearSelectedEmployees())
      dispatch(clearSelectedServices())
      dispatch(clearSelectedAvailableTimes())
    }
  }

  const handleLoginSubmit = async () => {
    setLoginLoading(true)
    await submitLogin({ email: loginEmail, password: loginPassword, salonGuid, setErrors: setLoginError, dispatch })
    setLoginLoading(false)
  }

  return (
    <>
      {loading && <Loading />}
      {!isUserLoggedIn && (
        <>
          <Box margin="0 0 1rem">
            <Card>
              <Box margin="0 0 1rem">
                <Typography variant="h4" margin="0">
                  Login
                </Typography>
              </Box>
              <LoginForm error={loginError} setEmail={setLoginEmail} setPassword={setLoginPassword} />
              <SubmitButton disabled={loginLoading} onClick={handleLoginSubmit}>
                Login
              </SubmitButton>
            </Card>
          </Box>
          <Card>
            <Box margin="0 0 1rem">
              <Typography variant="h4" margin="0">
                Ihre Daten
              </Typography>
            </Box>
            <Input name="firstName" label="Vorname" value={firstName} onChange={setFirstName} />
            <Input name="lastName" label="Nachname" value={lastName} onChange={setLastName} />
            <PhoneInput phone={phone} setPhone={setPhone} />
            <Input name="email" label="Email" type="email" value={email} onChange={setEmail} />
            <GenderSelect onChange={setGender} />
            <DateSelect onDayChange={setBirthdayDay} onMonthChange={setBirthdayMonth} onYearChange={setBirthdayYear} />
          </Card>
        </>
      )}
      <Card>
        <Box margin="0 0 1rem">
          <Typography variant="h4" margin="0">
            Buchung Details
          </Typography>
        </Box>
        <Separator />
        <Box flex centerVertical padding="1rem 0">
          <Box>
            <EmployeeAvatar src={selectedEmployee.fotoUrl} alt={getEmployeeFullName(selectedEmployee.vorname, selectedEmployee.nachname)} />
          </Box>
          <Box>
            <Typography variant="body2" margin="0.25rem">
              {selectedEmployee.title}
            </Typography>
            <Typography variant="body1" margin="0.25rem">
              {getEmployeeFullName(selectedEmployee.vorname, selectedEmployee.nachname)}
            </Typography>

            <Typography variant="body2" margin="0.25rem">
              {getFullAppointmentDateAndTime()}
            </Typography>
          </Box>
        </Box>
        <Separator />
        <Box margin="1rem 0 0.5rem">
          <Typography variant="bold" margin="">
            {getTotalServicesPrice(selectedServices)} €
          </Typography>
        </Box>
        {selectedServices.map(service => (
          // eslint-disable-next-line react/no-array-index-key
          <Box flex centerVertical key={service.guid}>
            <Box margin="0 0.5rem 0 0">
              <Typography variant="body1" margin="0">
                {service.name}
              </Typography>
            </Box>
            <Typography variant="body2" margin="0">
              {service.minuten} min.
            </Typography>
          </Box>
        ))}
      </Card>
      {error && (
        <Box padding="1rem 1.5rem">
          <ErrorMessage>{error}</ErrorMessage>
        </Box>
      )}
      {!isUserLoggedIn && (
        <Box padding="1rem 1.5rem">
          <Input name="newsletter" label="Newsletter anmelden" type="checkbox" value={newsletter} onChange={toggleNewsletter} />
          <PrivacyPolicyCheckbox value={privacyPolicy} onChange={togglePrivacyPolicy} />
        </Box>
      )}
      {isUserAllowedForOnlineBooking ? (
        <Box margin="1rem 0 0">
          <SubmitButton onClick={confirmBooking}>Buchen</SubmitButton>
        </Box>
      ) : (
        <Box flex flexDirection="column" centerVertical margin="2rem 1rem">
          <Typography variant="h3" margin="0" textAlign="center">
            Der Termin ist nicht online buchbar.
          </Typography>
          <Typography variant="body1">Nur telefonisch möglich.</Typography>
          <ActionButton>
            <a href={`tel:${salonData ? salonData.kontakt.tel : '0'}`}>Anrufen</a>
          </ActionButton>
        </Box>
      )}
    </>
  )
}

const mapStateToProps = ({ employees, availableTimes, services, salon }: ApplicationState) => ({
  selectedEmployee: employees.selected,
  selectedDate: availableTimes.selectedDate,
  selectedTime: availableTimes.selectedTime,
  selectedServices: services.selected,
  salonGuid: salon.data?.guid
})

export default connect(mapStateToProps)(BookingConfirmation)
